import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MaskedCardPipe } from 'src/infrastructure/pipes/masked-card.pipe';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { AutoUnsubscribe } from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReconciliationModel } from '../../../models/client-reconciliation';
import { AdxPaymentService } from '../../../service/adx-payment.service';

@AutoUnsubscribe()
@Component({
    selector: 'reconciliation-payments-edit-modal',
    templateUrl: './reconciliation-payments-edit-modal.component.html',
    styleUrls: ['./reconciliation-payments-edit-modal.component.scss'],
    standalone: false
})
export class ReconciliationPaymentsEditModalComponent implements OnInit {
  displayedColumns: string[] = [
    'adxInvoiceId',
    'adxBookingId',
    'adxBookingTravelTypeName',
    'adxBookingVendorName',
    'totalFare',
    'vendorPaymentMadeAmt',
    'clientPaymentReceivedAmt'
  ];

  loadedData = false;
  submitedData = true;
  dataSource: any[] = [];
  formControlNames: { [key: string]: { totalFare: string, vendorPaymentMadeAmt: string, clientPaymentReceivedAmt: string } } = {};
  formChanges$?: Subscription;
  dynamicFormGroup?: FormGroup;
  initialValues: Record<string, string> = {};
  hasChanges = false;
  amountPattern = /^-?\d{1,10}(\.\d{1,4})?$/;

  pagination: Partial<PageEvent & { totalItemCount?: number }> = {};
  sorting: Partial<Sort> = {};

  constructor(
    @Optional() public dialogRef: MatDialogRef<ReconciliationPaymentsEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReconciliationModel,
    private adxPaymentService: AdxPaymentService,
    private maskedCardPipe: MaskedCardPipe,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    if (!this.data) this.dialogRef.close();

    this.pagination = {
      pageSize: this.pagination.pageSize || 25,
      pageIndex: 0,
      totalItemCount: 0
    };

    this.getItems();
  }

  buildForm(): void {
    const formControllers = this.getFormControlers();
    this.dynamicFormGroup = new FormGroup(formControllers);

    this.dataSource.forEach(payment => {
      const adxBookingId = payment.adxBookingId;

      if (!this.formControlNames[adxBookingId]) {
        this.formControlNames[adxBookingId] = {
          totalFare: `totalFare_${adxBookingId}`,
          vendorPaymentMadeAmt: `vendorPaymentMadeAmt_${adxBookingId}`,
          clientPaymentReceivedAmt: `clientPaymentReceivedAmt_${adxBookingId}`
        };
      }

      const names = this.formControlNames[adxBookingId];

      this.initialValues[names.totalFare] = payment.totalFare;
      this.initialValues[names.vendorPaymentMadeAmt] = payment.vendorPaymentMadeAmt;
      this.initialValues[names.clientPaymentReceivedAmt] = payment.clientPaymentReceivedAmt;

      this.dynamicFormGroup?.addControl(names.totalFare, new FormControl(payment.totalFare));
      this.dynamicFormGroup?.addControl(names.vendorPaymentMadeAmt, new FormControl(payment.vendorPaymentMadeAmt));
      this.dynamicFormGroup?.addControl(names.clientPaymentReceivedAmt, new FormControl(payment.clientPaymentReceivedAmt));
    });
  }

  getFormControlers(): Record<string, FormControl> {
    const formList: Record<string, FormControl> = {};
    this.dataSource.forEach(payment => {
      const id = payment.adxBookingId;
      formList[`totalFare_${id}`] = new FormControl('', Validators.pattern(this.amountPattern));
      formList[`vendorPaymentMadeAmt_${id}`] = new FormControl('', Validators.pattern(this.amountPattern));
      formList[`clientPaymentReceivedAmt_${id}`] = new FormControl('', Validators.pattern(this.amountPattern));
    });
    return formList;
  }

  handleSortChange(sort: Sort): void {
    this.sorting = sort.active && sort.direction ? sort : {};
    this.getItems();
  }

  handlePageChanged(page: PageEvent): void {
    this.pagination = { ...this.pagination, ...page };
    this.getItems();
  }

  getItems(): void {
    this.loadedData = false;
    this.adxPaymentService.searchAdxReconciliation({
      filters: {
        ccNoLastFourDigits: this.maskedCardPipe.transform(this.data.lastFour, ''),
        tripExternalReference: this.data.adxReference,
        vendorName: this.data.vendorName,
        reconType: this.data.reconType
      },
      page: this.pagination,
      sort: this.sorting
    }).subscribe(data => {
      this.dataSource = data?.items || [];
      this.pagination = {
        ...this.pagination,
        pageIndex: data.pagination?.pageNumber ? data.pagination.pageNumber - 1 : 0,
        pageSize: data.pagination?.pageSize || this.pagination.pageSize || 25,
        totalItemCount: data.pagination?.totalItemCount || this.dataSource.length
      };
      this.loadedData = true;
      this.buildForm();
    });
  }

  isDisabled(): boolean {
    return !this.dataSource.some(payment => {
      const id = payment.adxBookingId;
      const names = this.formControlNames[id];

      const totalFare = Number(this.dynamicFormGroup?.get(names.totalFare)?.value || this.initialValues[names.totalFare]);
      const vendorAmt = Number(this.dynamicFormGroup?.get(names.vendorPaymentMadeAmt)?.value || this.initialValues[names.vendorPaymentMadeAmt]);
      const clientAmt = Number(this.dynamicFormGroup?.get(names.clientPaymentReceivedAmt)?.value || this.initialValues[names.clientPaymentReceivedAmt]);

      if (isNaN(totalFare) || isNaN(vendorAmt) || isNaN(clientAmt)) {
        return false;
      }

      return totalFare !== Number(this.initialValues[names.totalFare]) ||
        vendorAmt !== Number(this.initialValues[names.vendorPaymentMadeAmt]) ||
        clientAmt !== Number(this.initialValues[names.clientPaymentReceivedAmt]);
    });
  }

  updateAmounts(): void {
    this.submitedData = false;
    const updates$: Observable<any>[] = [];

    const changedPayments = this.dataSource.filter(payment => {
      const id = payment.adxBookingId;
      const names = this.formControlNames[id];

      const totalFare = Number(this.dynamicFormGroup?.get(names.totalFare)?.value || this.initialValues[names.totalFare]);
      const vendorAmt = Number(this.dynamicFormGroup?.get(names.vendorPaymentMadeAmt)?.value || this.initialValues[names.vendorPaymentMadeAmt]);
      const clientAmt = Number(this.dynamicFormGroup?.get(names.clientPaymentReceivedAmt)?.value || this.initialValues[names.clientPaymentReceivedAmt]);

      return totalFare !== Number(this.initialValues[names.totalFare]) ||
        vendorAmt !== Number(this.initialValues[names.vendorPaymentMadeAmt]) ||
        clientAmt !== Number(this.initialValues[names.clientPaymentReceivedAmt]);
    });

    changedPayments.forEach(payment => {
      const id = payment.adxBookingId;
      const names = this.formControlNames[id];

      const totalFare = Number(this.dynamicFormGroup?.get(names.totalFare)?.value || this.initialValues[names.totalFare]);
      const vendorAmt = Number(this.dynamicFormGroup?.get(names.vendorPaymentMadeAmt)?.value || this.initialValues[names.vendorPaymentMadeAmt]);
      const clientAmt = Number(this.dynamicFormGroup?.get(names.clientPaymentReceivedAmt)?.value || this.initialValues[names.clientPaymentReceivedAmt]);

      updates$.push(this.adxPaymentService.adjustmentAdxPaymentReconciliation({
        adxBookingId: id,
        amount: totalFare !== Number(this.initialValues[names.totalFare]) ? totalFare - Number(this.initialValues[names.totalFare]) : null,
        adxTripId: this.dataSource[0].tripId,
        bankId: this.data.fmsBankId,
        branchId: this.data.fmsBranchId,
        ckCcNo: this.data.originalCkCcNo,
        payeeName: this.data.fmsPayeeName,
        vendorPaymentMadeAmt: vendorAmt !== Number(this.initialValues[names.vendorPaymentMadeAmt]) ? vendorAmt - Number(this.initialValues[names.vendorPaymentMadeAmt]) : null,
        clientPaymentReceivedAmt: clientAmt !== Number(this.initialValues[names.clientPaymentReceivedAmt]) ? clientAmt - Number(this.initialValues[names.clientPaymentReceivedAmt]) : null
      }));
    });

    if (updates$.length > 0) {
      forkJoin(updates$).subscribe({
        next: () => {
          this.snackBar.open('Service adjusted successfully', 'Dismiss', { horizontalPosition: 'center', verticalPosition: 'bottom', duration: 3000 });
          this.getItems();
          this.submitedData = true;
        },
        error: () => this.submitedData = true
      });
    } else {
      this.snackBar.open('No changes detected', 'Dismiss', { horizontalPosition: 'center', verticalPosition: 'bottom', duration: 3000 });
      this.submitedData = true;
    }
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
